import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Input } from "@replo/design-system/components/input/Input";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "replo-utils/lib/misc";

type FormValues = {
  name: string;
};

export const AddWorkspaceModal = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { mutate: createWorkspace, isPending: isLoadingCreation } =
    workspaceRouter.create.useMutation({
      onSuccess: (data) => {
        toast({ title: "Workspace created" });
        void workspaceUtils.getUserWorkspacesList.invalidate();
        void projectUtils.listGroupedByWorkspaceForUser.invalidate();
        navigate(
          `/workspace/${data.workspace.id}/settings/general?new-shop=true`,
        );
      },
      onSettled: () => {
        onOpenChange(false);
      },
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
    },
  });
  const nameValue = useWatch({ control, name: "name" });
  const hasNameErrors = !isEmpty(errors.name);

  const { user } = useCurrentUser();

  const onSubmit = ({ name }: FormValues) => {
    if (!user) {
      return;
    }
    void createWorkspace({
      name,
      paymentProcessor: "stripe",
      eventOrigin: "dropdown",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const footer = (
    <div className="flex justify-end w-full">
      <Button
        variant="primary"
        size="base"
        render={<button type="submit" form="add-workspace-form" />}
        loading={isLoadingCreation}
        disabled={hasNameErrors || !nameValue}
      >
        Add
      </Button>
    </div>
  );

  const formContent = (
    <form
      id="add-workspace-form"
      onSubmit={(data) => {
        void handleSubmit(onSubmit)(data);
      }}
    >
      <LabeledControl label="Workspace Name" error={errors.name?.message}>
        <Input
          id="workspaceName"
          autoComplete="off"
          placeholder="Enter workspace name..."
          {...register("name", {
            required: "Please enter the workspace name.",
          })}
          type="text"
          size="base"
          invalid={hasNameErrors}
          autoFocus={!isMobile}
        />
      </LabeledControl>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Add Workspace"
        footer={footer}
      >
        {formContent}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Workspace"
      size="base"
      footer={footer}
    >
      {formContent}
    </Modal>
  );
};
